import { Controller, useFormContext } from "react-hook-form";
import { DuplicateProductFormValues } from "./types";
import {
  Button,
  Card,
  FlatActionList,
  Label,
  Stack,
  Text,
  TextField,
} from "@towersystems/roam-common-native";
import { Ionicons } from "@expo/vector-icons";
import { View } from "react-native-ui-lib";
import React from "react";
import { Switch } from "react-native";

export interface DuplicateProductFormProps {
  onGenerateBarcodePress?: () => Promise<string | undefined | void>;
}

export const DuplicateProductForm = ({
  onGenerateBarcodePress,
}: DuplicateProductFormProps) => {
  const { control, setValue, formState } =
    useFormContext<DuplicateProductFormValues>();

  const { errors } = formState;

  const handleOnGenerateBarcodePress = React.useCallback(async () => {
    if (!onGenerateBarcodePress) {
      return;
    }

    const barcode = await onGenerateBarcodePress();
    if (barcode) {
      setValue("newBarcode", barcode, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [setValue, onGenerateBarcodePress]);

  const trailingMarkup = onGenerateBarcodePress ? (
    <Button size="compact" onPress={handleOnGenerateBarcodePress}>
      <Text color="action">
        <Ionicons name="barcode" size={24} />
      </Text>
    </Button>
  ) : undefined;

  return (
    <Stack vertical>
      <Card title="New Product Details">
        <Stack vertical>
          <View>
            <Label required label="Product Name"></Label>
            <Controller
              name="newTitle"
              control={control}
              render={({
                field: { onChange, value, onBlur },
                fieldState: { error },
              }) => (
                <TextField
                  onBlur={onBlur}
                  value={value ?? ""}
                  onChangeText={onChange}
                  validationMessage={error ? error.message : undefined}
                  placeholder="Enter an product name"
                />
              )}
            />
          </View>
          <View>
            <Label required label="Barcode"></Label>
            <Controller
              name="newBarcode"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TextField
                  value={value ?? ""}
                  onChangeText={onChange}
                  validationMessage={
                    error
                      ? error.message
                      : errors.root?.stockNumber?.message ?? undefined
                  }
                  placeholder="Enter a barcode"
                  trailingAccessory={trailingMarkup}
                />
              )}
            />
          </View>
        </Stack>
      </Card>
      <Card title="Select Details to Copy">
        <FlatActionList
          items={[
            {
              key: "copyPricing",
              title: "Copy Pricing",
              subTitle: "All pricing will be copied to the new product",
              onPress: () => {},
              componentRight: () => (
                <Controller
                  name="copyPricing"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <View>
                      <Switch
                        value={value}
                        onValueChange={() => {
                          onChange(!value);
                        }}
                      />
                    </View>
                  )}
                />
              ),
            },

            {
              key: "copyProductTemplate",
              title: "Copy Product Variant Template",
              subTitle:
                "All product variant template will be copied to the new product",
              onPress: () => {},
              componentRight: () => (
                <Controller
                  name="copyProductTemplate"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <View>
                      <Switch
                        value={value}
                        onValueChange={() => {
                          onChange(!value);
                        }}
                      />
                    </View>
                  )}
                />
              ),
            },

            {
              key: "copySupplier",
              title: "Copy Supplier",
              subTitle: "Supplier will be copied to the new product",
              onPress: () => {},
              componentRight: () => (
                <Controller
                  name="copySupplier"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <View>
                      <Switch
                        value={value}
                        onValueChange={() => {
                          onChange(!value);
                        }}
                      />
                    </View>
                  )}
                />
              ),
            },
          ]}
        />
      </Card>
    </Stack>
  );
};
